import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, Calendar, Check, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AcademicYearPage = () => {
  const { getAuthHeader } = useAuth();
  const [sessions, setSessions] = useState([]);
  
  const [sessionDialogOpen, setSessionDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  
  const [newSession, setNewSession] = useState({
    session_name: '',
    start_date: '',
    end_date: '',
    start_month: 4,  // April
    end_month: 3     // March
  });
  
  const [editSession, setEditSession] = useState({
    start_date: '',
    end_date: '',
    start_month: 4,
    end_month: 3
  });
  
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const sessionsRes = await axios.get(`${API_URL}/api/academic/sessions`, { headers: getAuthHeader() });
      setSessions(sessionsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load academic data');
    }
  };

  const handleCreateSession = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/academic/sessions`, newSession, { headers: getAuthHeader() });
      toast.success('Academic session created successfully!');
      setSessionDialogOpen(false);
      setNewSession({ session_name: '', start_date: '', end_date: '', start_month: 4, end_month: 3 });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create session');
    } finally {
      setLoading(false);
    }
  };

  const handleSetCurrentSession = async (sessionId) => {
    try {
      await axios.put(
        `${API_URL}/api/academic/sessions/${sessionId}/set-current`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success('Current session updated!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update session');
    }
  };

  const handleEditSession = (session) => {
    setEditingSession(session);
    setEditSession({
      start_date: session.start_date || '',
      end_date: session.end_date || '',
      start_month: session.start_month || 4,
      end_month: session.end_month || 3
    });
    setEditDialogOpen(true);
  };

  const handleUpdateSession = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.put(
        `${API_URL}/api/academic/sessions/${editingSession.id}`,
        editSession,
        { headers: getAuthHeader() }
      );
      toast.success('Session updated successfully!');
      setEditDialogOpen(false);
      setEditingSession(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update session');
    } finally {
      setLoading(false);
    }
  };

  const currentSession = sessions.find(s => s.is_current);

  return (
    <DashboardLayout title="Academic Year Management">
      <div className="space-y-6">
        {/* Current Session Banner */}
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-sm">Current Academic Session</p>
                  <h2 className="text-3xl font-bold mt-1">
                    {currentSession?.session_name || 'Not Set'}
                  </h2>
                  {currentSession && (
                    <p className="text-blue-100 text-sm mt-1">
                      {currentSession.start_date} to {currentSession.end_date}
                    </p>
                  )}
                </div>
                <Calendar className="h-16 w-16 text-blue-200 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <Tabs defaultValue="sessions" className="space-y-4">
          <TabsList>
            <TabsTrigger value="sessions">Academic Sessions</TabsTrigger>
          </TabsList>

          {/* Sessions Tab */}
          <TabsContent value="sessions">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Academic Sessions</CardTitle>
                  <CardDescription>Manage your school&apos;s academic years</CardDescription>
                </div>
                <Dialog open={sessionDialogOpen} onOpenChange={setSessionDialogOpen}>
                  <DialogTrigger asChild>
                    <Button data-testid="create-session-btn">
                      <Plus className="h-4 w-4 mr-2" />
                      Add Session
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Create New Academic Session</DialogTitle>
                      <DialogDescription>Add a new academic year/session</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleCreateSession} className="space-y-4">
                      <div className="space-y-2">
                        <Label>Session Name *</Label>
                        <Input
                          placeholder="2025-2026"
                          value={newSession.session_name}
                          onChange={(e) => {
                            const val = e.target.value;
                            // Auto-format: if user types 4 digits, add hyphen
                            if (/^\d{4}$/.test(val) && !val.includes('-')) {
                              const nextYear = parseInt(val) + 1;
                              setNewSession({ ...newSession, session_name: `${val}-${nextYear}` });
                            } else {
                              setNewSession({ ...newSession, session_name: val });
                            }
                          }}
                          pattern="\d{4}-\d{4}"
                          title="Format: YYYY-YYYY (e.g., 2025-2026)"
                          data-testid="session-name-input"
                          required
                        />
                        <p className="text-xs text-muted-foreground">Format: YYYY-YYYY (e.g., 2025-2026). Type start year, hyphen will auto-add.</p>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Start Date *</Label>
                          <Input
                            type="date"
                            value={newSession.start_date}
                            onChange={(e) => setNewSession({ ...newSession, start_date: e.target.value })}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>End Date *</Label>
                          <Input
                            type="date"
                            value={newSession.end_date}
                            onChange={(e) => setNewSession({ ...newSession, end_date: e.target.value })}
                            required
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Fee Start Month</Label>
                          <Select 
                            value={String(newSession.start_month)} 
                            onValueChange={(v) => setNewSession({ ...newSession, start_month: parseInt(v) })}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="1">January</SelectItem>
                              <SelectItem value="2">February</SelectItem>
                              <SelectItem value="3">March</SelectItem>
                              <SelectItem value="4">April</SelectItem>
                              <SelectItem value="5">May</SelectItem>
                              <SelectItem value="6">June</SelectItem>
                              <SelectItem value="7">July</SelectItem>
                              <SelectItem value="8">August</SelectItem>
                              <SelectItem value="9">September</SelectItem>
                              <SelectItem value="10">October</SelectItem>
                              <SelectItem value="11">November</SelectItem>
                              <SelectItem value="12">December</SelectItem>
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-muted-foreground">Monthly fees will start from this month</p>
                        </div>
                        <div className="space-y-2">
                          <Label>Fee End Month</Label>
                          <Select 
                            value={String(newSession.end_month)} 
                            onValueChange={(v) => setNewSession({ ...newSession, end_month: parseInt(v) })}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="1">January</SelectItem>
                              <SelectItem value="2">February</SelectItem>
                              <SelectItem value="3">March</SelectItem>
                              <SelectItem value="4">April</SelectItem>
                              <SelectItem value="5">May</SelectItem>
                              <SelectItem value="6">June</SelectItem>
                              <SelectItem value="7">July</SelectItem>
                              <SelectItem value="8">August</SelectItem>
                              <SelectItem value="9">September</SelectItem>
                              <SelectItem value="10">October</SelectItem>
                              <SelectItem value="11">November</SelectItem>
                              <SelectItem value="12">December</SelectItem>
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-muted-foreground">Monthly fees will end at this month</p>
                        </div>
                      </div>
                      <div className="flex justify-end gap-2">
                        <Button type="button" variant="outline" onClick={() => setSessionDialogOpen(false)}>
                          Cancel
                        </Button>
                        <Button type="submit" disabled={loading} data-testid="submit-session-btn">
                          {loading ? 'Creating...' : 'Create Session'}
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                {sessions.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No academic sessions found. Create one to get started.</p>
                ) : (
                  <div className="space-y-3">
                    {sessions.map((session, index) => (
                      <motion.div
                        key={session.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className={`flex items-center justify-between p-4 rounded-lg border ${
                          session.is_current ? 'border-blue-500 bg-blue-50' : 'bg-card'
                        }`}
                      >
                        <div className="flex items-center gap-4">
                          <div className={`p-2 rounded-full ${session.is_current ? 'bg-blue-100' : 'bg-muted'}`}>
                            <Calendar className={`h-5 w-5 ${session.is_current ? 'text-blue-600' : 'text-muted-foreground'}`} />
                          </div>
                          <div>
                            <h3 className="font-semibold flex items-center gap-2">
                              {session.session_name}
                              {session.is_current && (
                                <span className="text-xs bg-blue-600 text-white px-2 py-0.5 rounded-full">Current</span>
                              )}
                            </h3>
                            <p className="text-sm text-muted-foreground">
                              {session.start_date} to {session.end_date}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              Fee Period: {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][(session.start_month || 4) - 1]} - {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][(session.end_month || 3) - 1]}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditSession(session)}
                            data-testid={`edit-session-btn-${session.id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          {!session.is_current && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleSetCurrentSession(session.id)}
                              data-testid={`set-current-btn-${session.id}`}
                            >
                              <Check className="h-4 w-4 mr-1" />
                              Set as Current
                            </Button>
                          )}
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Note about Student Promotion */}
        <Card className="mt-4 border-blue-200 bg-blue-50/50">
          <CardContent className="py-4">
            <p className="text-sm text-blue-800">
              💡 <strong>Note:</strong> For student promotion and batch processing, please go to{' '}
              <a href="/promotion" className="text-blue-600 underline font-medium">Student Promotion</a> page.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Edit Session Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Academic Session</DialogTitle>
            <DialogDescription>
              Update session details for {editingSession?.session_name}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateSession} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={editSession.start_date}
                  onChange={(e) => setEditSession({ ...editSession, start_date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={editSession.end_date}
                  onChange={(e) => setEditSession({ ...editSession, end_date: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Fee Start Month</Label>
                <Select 
                  value={String(editSession.start_month)} 
                  onValueChange={(v) => setEditSession({ ...editSession, start_month: parseInt(v) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">January</SelectItem>
                    <SelectItem value="2">February</SelectItem>
                    <SelectItem value="3">March</SelectItem>
                    <SelectItem value="4">April</SelectItem>
                    <SelectItem value="5">May</SelectItem>
                    <SelectItem value="6">June</SelectItem>
                    <SelectItem value="7">July</SelectItem>
                    <SelectItem value="8">August</SelectItem>
                    <SelectItem value="9">September</SelectItem>
                    <SelectItem value="10">October</SelectItem>
                    <SelectItem value="11">November</SelectItem>
                    <SelectItem value="12">December</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">Monthly fees start from this month</p>
              </div>
              <div className="space-y-2">
                <Label>Fee End Month</Label>
                <Select 
                  value={String(editSession.end_month)} 
                  onValueChange={(v) => setEditSession({ ...editSession, end_month: parseInt(v) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">January</SelectItem>
                    <SelectItem value="2">February</SelectItem>
                    <SelectItem value="3">March</SelectItem>
                    <SelectItem value="4">April</SelectItem>
                    <SelectItem value="5">May</SelectItem>
                    <SelectItem value="6">June</SelectItem>
                    <SelectItem value="7">July</SelectItem>
                    <SelectItem value="8">August</SelectItem>
                    <SelectItem value="9">September</SelectItem>
                    <SelectItem value="10">October</SelectItem>
                    <SelectItem value="11">November</SelectItem>
                    <SelectItem value="12">December</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">Monthly fees end at this month</p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Updating...' : 'Update Session'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default AcademicYearPage;
